<template>
  <div v-if="item && modelValue" class="relative h-full pb-4 bg-white">
    <table class="relative z-0 w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
      <tbody>
        <BaseDetailsTableRow
          :mode="currentMode"
          :name="$t('customer.traderTaxNumber')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="currentMode"
            v-if="modelValue.trader"
            @enter-keydown="onTaxNoSearch"
            @debounced-input-changed="onTaxNoInputChanged"
            icon="right"
            v-model="modelValue.trader.taxNumber"
            :color="modelValue.trader.isFromAADE ? 'green' : 'base'"
            flat
            show-error-as-tooltip
            :error="
              validations?.traderTaxNo.required.$invalid
                ? validations?.traderTaxNo.required.$message
                : ''
            ">
            <template #icon-right>
              <div class="flex items-center gap-2 pr-3.5">
                <span
                  v-if="modelValue.trader.isFromAADE"
                  class="text-white bg-green-600 text-xs font-normal px-2.5 py-0.5 rounded-full border border-theme-400"
                  :class="{ 'bg-green-600': modelValue.trader.isFromAADE }">
                  AADE
                </span>
                <svg
                  v-if="currentMode === 'edit'"
                  class="z-20 w-5 h-5 text-gray-400 cursor-pointer"
                  :class="modelValue.trader.isFromAADE ? 'text-green-600' : 'text-gray-400'"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  @click="onTaxNoSearch()"
                  aria-hidden="true">
                  <path
                    fill-rule="evenodd"
                    d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                    clip-rule="evenodd" />
                </svg>
              </div>
            </template>
          </BaseInput>
        </BaseDetailsTableRow>
        <BaseDetailsTableRow
          :name="$t('customer.traderName')"
          :mode="currentMode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-if="modelValue.trader"
            :mode="currentMode"
            v-model="modelValue.trader.name"
            color="base"
            flat
            show-error-as-tooltip
            :error="
              validations?.traderName.required.$invalid
                ? validations?.traderName.required.$message
                : ''
            " />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :mode="currentMode"
          :name="$t('customer.traderCode')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-if="modelValue.trader"
            :mode="currentMode"
            v-model="modelValue.trader.code"
            color="base"
            flat
            show-error-as-tooltip
            :error="
              validations?.traderCode.required.$invalid
                ? validations?.traderCode.required.$message
                : ''
            " />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :mode="currentMode"
          :name="$t('customer.traderJob')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-if="modelValue.trader"
            :mode="currentMode"
            v-model="modelValue.trader.job"
            color="base"
            flat />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :mode="currentMode"
          :name="$t('customer.traderCity')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-if="modelValue.trader"
            :mode="currentMode"
            v-model="modelValue.trader.city"
            color="base"
            flat />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :mode="currentMode"
          :name="$t('customer.traderAddress')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-if="modelValue.trader"
            :mode="currentMode"
            v-model="modelValue.trader.address"
            color="base"
            flat />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :mode="currentMode"
          :name="$t('customer.traderZipCode')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-if="modelValue.trader"
            :mode="currentMode"
            v-model="modelValue.trader.zipCode"
            color="base"
            flat />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          v-if="mode === 'edit' && !props.item?.trader?.taxNumber"
          :mode="currentMode"
          :name="$t('customer.currentTraderSiteAddress')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-if="modelValue.trader"
            :mode="currentMode"
            v-model="modelValue.currentTraderSiteAddress"
            color="base"
            flat />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          v-if="mode === 'edit' && !props.item?.trader?.taxNumber"
          :mode="currentMode"
          :name="$t('customer.currentTraderSiteTaxCode')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-if="modelValue.trader"
            :mode="currentMode"
            v-model="modelValue.currentTraderSiteTaxCode"
            color="base"
            flat />
        </BaseDetailsTableRow>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import BaseInput from '../common/BaseInput.vue';
import BaseDetailsTableRow from '../common/BaseDetailsTableRow.vue';
import { type Customer, type Representative, type Trader } from '@/types/Traders';
import { findTraderByTaxNo } from '@/services/api/traders';
import type { Validation } from '@vuelidate/core';
import { computed } from 'vue';

const props = defineProps<{
  item: Customer | Representative | undefined;
  mode: 'view' | 'edit';
  isHorizontalLayout: boolean;
  validations?: Validation;
}>();

const modelValue = defineModel<Customer | Representative>();

async function onTaxNoSearch() {
  if (modelValue.value?.trader?.taxNumber) {
    const trader = await findTraderByTaxNo(modelValue.value?.trader?.taxNumber);
    if (trader) {
      modelValue.value.trader = trader;
      modelValue.value.traderId = trader.id;
      modelValue.value.code = trader.code;
      if ('profession' in modelValue.value) modelValue.value.profession = trader.job;
    } else {
      initTrader();
    }
  }
}

function onTaxNoInputChanged() {
  initTrader();
}

function initTrader() {
  if (modelValue.value?.trader) {
    modelValue.value.trader = {
      id: modelValue.value.trader.id,
      taxNumber: modelValue.value.trader.taxNumber,
      name: '',
      code: '',
    } as Trader;
  }
}

const currentMode = computed(() => (props.item?.trader?.taxNumber ? 'view' : props.mode));
</script>
